import Autocomplete from '@mui/material/Autocomplete'
import { orderByField } from '@src/utils/array.utils'
import cx from 'classnames'
import * as React from 'react'
import styles from './select.component.styl'

import TextField from '@mui/material/TextField'

export interface SelectOption<T> {
  disabled?: boolean
  label?: string
  value?: ValueType<T>
}

export interface OwnProps<T> {
  classNameItem?: string
  name?: string
  disablePortal?: boolean
  placeholder?: string
  isDisabled?: boolean
  isClearable?: boolean
  role?: string
  className?: string
  useAll?: boolean
  width?: string | number
  alphabetically?: boolean
  options: SelectOption<T>[]
  value: SelectOption<T>
  onChange: (value: SelectOption<T>) => void
}

export type ValueType<T> = T | null | undefined

export type Props<T> = OwnProps<T>

function getOptions<T extends string | number | string[]>(
  options: SelectOption<T>[],
  alphabetically: boolean
) {
  return alphabetically ? orderByField({ options, key: 'label' }) : options
}

function SelectComponent<T extends string | number | string[]>(
  props: Props<T>
) {
  const {
    options,
    useAll,
    name,
    disablePortal,
    placeholder = 'Select...',
    isDisabled,
    isClearable = false,
    onChange,
    width = 'auto',
    classNameItem,
    role = 'dropDownOptions',
    value,
    className,
    alphabetically = true
  } = props || ({} as Props<T>)
  let selectOptions: SelectOption<T>[] = useAll
    ? ([{ value: 'ALL', label: 'ALL' }] as SelectOption<T>[])
    : ([] as SelectOption<T>[])
  selectOptions = [...selectOptions, ...getOptions(options, alphabetically)]

  function handleChange(event: React.ChangeEvent<{}>, value) {
    if (typeof onChange === 'function') {
      onChange(value)
    }
  }

  return (
    <div role={role} className={cx(className, styles.selectWrapper)}>
      <Autocomplete
        className={styles.root}
        size="small"
        classes={{
          popper: styles.popper,
          paper: styles.paper,
          inputRoot: styles.inputRoot,
          option: cx(styles.option, classNameItem)
        }}
        disabled={isDisabled}
        disableClearable={!isClearable || isDisabled}
        value={value}
        defaultValue={value}
        options={selectOptions}
        disablePortal={disablePortal}
        onChange={handleChange}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        getOptionLabel={option =>
          typeof option === 'string' ? option : option.label
        }
        style={{ width }}
        renderInput={params => (
          <TextField
            disabled={isDisabled}
            className={styles.inputField}
            {...params}
            name={name}
            variant="outlined"
            placeholder={placeholder}
            fullWidth
          />
        )}
      />
    </div>
  )
}

export default React.memo(SelectComponent)
